import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';

interface ApplicationRow {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  message: string | null;
  cv_url: string | null;
  cover_letter_url: string | null;
  status: string;
  created_at: string;
}

interface JobRow {
  id: string;
  title: string;
  company: string;
}

const statusLabels: Record<string, { text: string; cls: string }> = {
  pending: { text: 'Nouvelle', cls: 'bg-secondary-500/10 text-secondary-400' },
  reviewed: { text: 'Examinée', cls: 'bg-accent-500/10 text-accent-400' },
  accepted: { text: 'Retenue', cls: 'bg-primary-500/10 text-primary-400' },
  rejected: { text: 'Refusée', cls: 'bg-red-500/10 text-red-400' },
};

function JobApplications() {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [job, setJob] = useState<JobRow | null>(null);
  const [applications, setApplications] = useState<ApplicationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    if (!user || !id) return;
    setLoading(true);
    setError('');
    try {
      const { data: jobData, error: jobErr } = await supabase
        .from('jobs')
        .select('id, title, company')
        .eq('id', id)
        .maybeSingle();
      if (jobErr) throw jobErr;
      setJob(jobData as JobRow | null);

      const { data: apps, error: appErr } = await supabase
        .from('job_applications')
        .select('*')
        .eq('job_id', id)
        .order('created_at', { ascending: false });
      if (appErr) throw appErr;
      setApplications((apps as ApplicationRow[]) || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les candidatures.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id]);

  const updateStatus = async (applicationId: string, status: string) => {
    const { error: err } = await supabase
      .from('job_applications')
      .update({ status })
      .eq('id', applicationId);
    if (!err) {
      setApplications((prev) =>
        prev.map((a) => (a.id === applicationId ? { ...a, status } : a)),
      );
    }
  };

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <Link
            to="/dashboard/jobs"
            className="inline-flex items-center gap-1 text-sm text-foreground-400 hover:text-secondary-400 transition-colors mb-2"
          >
            <i className="ri-arrow-left-line" />
            Mes offres d&apos;emploi
          </Link>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50">
            Candidatures reçues
          </h1>
          {job && (
            <p className="text-sm text-foreground-400 mt-1">
              {job.title} · {job.company}
            </p>
          )}
        </div>
        <span className="px-3 py-1.5 rounded-full bg-secondary-500/10 text-secondary-400 text-sm font-semibold whitespace-nowrap">
          {applications.length} candidature{applications.length === 1 ? '' : 's'}
        </span>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400 mb-4">
          {error}
          <button onClick={load} className="ml-3 underline text-foreground-50">Réessayer</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-500/10">
            <i className="ri-loader-4-line text-2xl text-secondary-400 animate-spin" />
          </div>
        </div>
      ) : applications.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-secondary-500/10">
            <i className="ri-user-search-line text-3xl text-secondary-400" />
          </div>
          <h2 className="font-heading font-bold text-xl text-foreground-50 mb-2">Aucune candidature</h2>
          <p className="text-sm text-foreground-400">
            Aucun candidat n&apos;a encore postulé à cette offre.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {applications.map((app) => {
            const st = statusLabels[app.status] ?? statusLabels.pending;
            return (
              <div
                key={app.id}
                className="p-5 rounded-xl bg-background-100 border border-background-200/70"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-heading font-semibold text-foreground-50">{app.full_name}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${st.cls} whitespace-nowrap`}>
                        {st.text}
                      </span>
                    </div>
                    <p className="text-sm text-foreground-400 mt-1">
                      <i className="ri-mail-line mr-1" />
                      {app.email}
                      {app.phone ? (
                        <>
                          <i className="ri-phone-line ml-3 mr-1" />
                          {app.phone}
                        </>
                      ) : null}
                    </p>
                    {app.message && (
                      <p className="text-sm text-foreground-400 mt-2 leading-relaxed">{app.message}</p>
                    )}
                    <p className="text-xs text-foreground-500 mt-2">
                      Reçue le {new Date(app.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {app.cv_url && (
                      <a
                        href={app.cv_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-background-300/60 text-foreground-500 text-sm font-medium hover:bg-background-200/60 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-file-text-line" />
                        CV
                      </a>
                    )}
                    {app.cover_letter_url && (
                      <a
                        href={app.cover_letter_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-background-300/60 text-foreground-500 text-sm font-medium hover:bg-background-200/60 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-file-3-line" />
                        Lettre
                      </a>
                    )}
                    <select
                      value={app.status}
                      onChange={(e) => updateStatus(app.id, e.target.value)}
                      className="bg-background-50 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-50 outline-none focus:border-secondary-400"
                    >
                      <option value="pending">Nouvelle</option>
                      <option value="reviewed">Examinée</option>
                      <option value="accepted">Retenue</option>
                      <option value="rejected">Refusée</option>
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
}

export default JobApplications;